import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BUSINESS_TYPES, WEBSITE_TYPES, PACKAGE_OPTIONS } from "@/data/content";
import { Reveal, LineReveal } from "./motion/Reveal";
import { WHATSAPP_URL, WHATSAPP_DISPLAY } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", business_name: "", email: "", phone: "", business_type: "", website_type: "", package: "", message: "" };

const Field = ({ label, id, children }) => (
  <div className="flex flex-col gap-1">
    <label htmlFor={id} className="font-mono text-[10px] uppercase tracking-[0.25em] text-taupe">{label}</label>
    {children}
  </div>
);

const Choose = ({ id, value, onChange, options, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger id={id} data-testid={`select-${id}`} className="field h-auto rounded-none border-x-0 border-t-0 px-0 focus:ring-0 focus:ring-offset-0 shadow-none data-[placeholder]:text-taupe/70">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="bg-ivory border-taupe-line rounded-none" data-lenis-prevent>
      {options.map((o) => <SelectItem key={o} value={o} data-testid={`option-${id}-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-none focus:bg-cream font-light">{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_type || !form.website_type || !form.package) {
      toast.error("Please complete every field so we can understand your project.");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/enquiry`, form);
      setStatus("sent");
      setForm(EMPTY);
      toast.success("Your enquiry has been sent. We'll be in touch shortly.");
    } catch (err) {
      setStatus("idle");
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "We couldn't send your enquiry. Please try again or reach us on WhatsApp.");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-y bg-cream relative overflow-hidden">
      <div className="absolute -right-32 -top-32 h-[520px] w-[520px] rounded-full bg-blush/60 blur-[140px] pointer-events-none" aria-hidden />
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 relative">
        <div className="lg:col-span-5">
          <Reveal><p className="eyebrow mb-6">Contact</p></Reveal>
          <LineReveal inView as="h2" lines={["Start Your", <><span className="italic text-sage">Project.</span></>]} className="h2" />
          <Reveal delay={0.2} className="body-lg mt-8">Tell us about your business, your vision, and the experience you want your customers to have. We read every enquiry personally and respond thoughtfully.</Reveal>
          <Reveal delay={0.3} className="mt-12 border-t border-charcoal/20 pt-8">
            <p className="eyebrow mb-4">Prefer WhatsApp?</p>
            <a data-testid="contact-whatsapp-link" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-3 font-serif text-3xl font-light text-charcoal hover:text-sage transition-colors">
              {WHATSAPP_DISPLAY} <ArrowUpRight size={20} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <p className="text-sm text-taupe mt-3 font-light">Bangalore, India</p>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form data-testid="contact-form" onSubmit={submit} className="bg-ivory border border-taupe-line p-7 sm:p-10 lg:p-12 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8" noValidate={false}>
            <Field label="Name" id="name"><input id="name" data-testid="input-name" required minLength={2} className="field" placeholder="Your name" value={form.name} onChange={set("name")} /></Field>
            <Field label="Business Name" id="business_name"><input id="business_name" data-testid="input-business-name" required className="field" placeholder="Your business" value={form.business_name} onChange={set("business_name")} /></Field>
            <Field label="Email" id="email"><input id="email" type="email" data-testid="input-email" required className="field" placeholder="you@business.com" value={form.email} onChange={set("email")} /></Field>
            <Field label="Phone Number" id="phone"><input id="phone" type="tel" data-testid="input-phone" required minLength={6} className="field" placeholder="+91" value={form.phone} onChange={set("phone")} /></Field>
            <Field label="Business Type" id="business-type"><Choose id="business-type" value={form.business_type} onChange={set("business_type")} options={BUSINESS_TYPES} placeholder="Select your industry" /></Field>
            <Field label="Website Type / Requirement" id="website-type"><Choose id="website-type" value={form.website_type} onChange={set("website_type")} options={WEBSITE_TYPES} placeholder="What do you need?" /></Field>
            <div className="sm:col-span-2"><Field label="Package Interested In" id="package"><Choose id="package" value={form.package} onChange={set("package")} options={PACKAGE_OPTIONS} placeholder="Choose a package" /></Field></div>
            <div className="sm:col-span-2"><Field label="Message" id="message"><textarea id="message" data-testid="input-message" required minLength={10} rows={4} className="field resize-none" placeholder="Tell us about your business, your goals and the experience you want to create." value={form.message} onChange={set("message")} /></Field></div>
            <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-5 pt-2">
              <button type="submit" data-testid="button-send-enquiry" disabled={status === "sending"} className="btn btn-dark disabled:opacity-60 min-w-[180px]">
                <span className="flex items-center gap-2">
                  {status === "sending" && <Loader2 size={15} className="animate-spin" />}
                  {status === "sent" && <Check size={15} />}
                  {status === "sending" ? "Sending…" : status === "sent" ? "Enquiry Sent" : "Send Enquiry"}
                </span>
              </button>
              <p className="text-xs text-taupe font-light">Your enquiry goes directly to the Amara Studios team. No spam, ever.</p>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
